import { ChangeDetectorRef, Component, Inject, OnInit, Optional, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, concatMap, forkJoin, from, of, takeUntil, tap } from 'rxjs';
import { catchError, filter, map, take } from 'rxjs/operators';
import { ErrorHandlerService } from 'src/app/api/error-handler.service';
import { AutoDrawService } from 'src/app/api/routes/autodraw.service';
import { ProductService } from 'src/app/api/routes/product.service';
import { BaseComponent } from 'src/app/components/base.component';
import { AUTO_DRAW_ENUM, EnumState } from 'src/app/core/enum/index';
import { AutoDrawModels, ProductModels } from 'src/app/core/models';
import { CloseAction, OpenAction, SpinnerState } from 'src/app/store/spinner';
import { CheckForm } from './check-form.service';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { GeneralService } from 'src/app/shared/general.service';
import { ContactUsComponent } from '../contact-us/contact-us.component';
import { AlertModalState, openAlertModal } from 'src/app/store/alert-modal';

@Component({
	selector: 'app-autodraw',
	templateUrl: './autodraw.component.html',
	styleUrls: ['./autodraw.component.scss']
})
export class AutodrawComponent extends BaseComponent implements OnInit {
	// 子元件
	@ViewChild(ContactUsComponent) contactUsComponent!: ContactUsComponent;

	// 備註圖片路徑
	public memoImg = 'assets/image/pulltab_memo.png';

	// 參數
	public params: AutoDrawModels.Tim[] = [];
	public project: AutoDrawModels.Project = new AutoDrawModels.Project();

	// enums
	public TIM_TYPE = AUTO_DRAW_ENUM.TIM_TYPE;
	public HARDNESS_TESTER = AUTO_DRAW_ENUM.HARDNESS_TESTER;
	public HARDNESS_TESTER_TOLERANCE = AUTO_DRAW_ENUM.HARDNESS_TESTER_TOLERANCE;
	public SHAPE = AUTO_DRAW_ENUM.SHAPE;
	public ADHESIVE = AUTO_DRAW_ENUM.ADHESIVE;
	public VIEW_ANGLE = AUTO_DRAW_ENUM.VIEW_ANGLE;
	public THERMAL_COEFFICIENT = AUTO_DRAW_ENUM.THERMAL_COEFFICIENT;
	public ALGO_TYPE = AUTO_DRAW_ENUM.ALGO_TYPE;
	public LAYER_CONSTANT = AUTO_DRAW_ENUM.LAYER_CONSTANT;
	public PRODUCT_CATEGORY = AUTO_DRAW_ENUM.PRODUCT_CATEGORY;
	public PULLTAB_LENGTH = AUTO_DRAW_ENUM.PULLTAB_LENGTH;
	public PULLTAB_CONSTANT = AUTO_DRAW_ENUM.PULLTAB_CONSTANT;
	public ADHESIVE_MODEL = AUTO_DRAW_ENUM.ADHESIVE_MODEL;
	public INCLUDE_THICKNESS = AUTO_DRAW_ENUM.INCLUDE_THICKNESS;


	// TIM 產品型號選項清單
	public modelOptions: {
		[key: string]: ProductModels.Product[]
	} = {}
	
	// 導熱係數選項清單
	public wmkOptions: {
		[key: string]: number[]
	} = {}

	// 各個手撕位選單
	public pulltabLengthLists: {
		[key: string]: number[]
	} = {}

	private enums: any;

	constructor(
		private autoDrawServ: AutoDrawService,
		private productServ: ProductService,
		private cd: ChangeDetectorRef,
		private spinnerState: Store<SpinnerState>,
		private errServ: ErrorHandlerService,
		private route: ActivatedRoute,
		private checkFormServ: CheckForm,
		private translate: TranslateService,
		private router: Router,
		private generalServ: GeneralService,
		private alertStore: Store<AlertModalState>,
		@Optional() @Inject('APP_INITIALIZED') private appInitialized$: BehaviorSubject<boolean>
	) { super(); }


	ngOnInit(): void {
		// 先初始化 enums 為空物件
		this.enums = {} as EnumState;

		// 使用 combineLatest 來同時監聽 isLoaded$ 和 enumSubject
		combineLatest([
			AUTO_DRAW_ENUM.isLoaded$,
			AUTO_DRAW_ENUM.enumSubject
		]).pipe(
			// 只在兩個值都ready時觸發
			filter(([isLoaded, enums]) => 
				isLoaded && !!enums && Object.keys(enums).length > 0
			),
			// 只取第一次的值
			take(1),
			takeUntil(this.unsubscribe$)
		).subscribe({
			next: ([_, data]) => {
				// console.log('Initial enum data loaded:', data);
				this.enums = data;
				this.updateEnumValues();
				this.cd.detectChanges();
		
				// 等 enum 載入完成後才執行初始化
				this.initializeComponent();
		
				// 設置後續更新的監聽
				AUTO_DRAW_ENUM.enumSubject
				.pipe(
					filter(enums => !!enums && Object.keys(enums).length > 0),
					takeUntil(this.unsubscribe$)
				)
				.subscribe(updatedData => {
					// console.log('Enum data updated:', updatedData);
					this.enums = updatedData;
					this.updateEnumValues();
					this.cd.detectChanges();
				});
			},
			error: (error) => {
				console.error('Failed to initialize enums:', error);
			}
		});
	  
		// 監控 isLoaded$ 的變化（僅用於調試）
		AUTO_DRAW_ENUM.isLoaded$.pipe(
		  	takeUntil(this.unsubscribe$)
		).subscribe(isLoaded => {
		  	// console.log('isLoaded$ value changed:', isLoaded);
		});
	}

	// 新增一個方法來更新所有使用到的 enum 值
	private updateEnumValues(): void {
		if (!this.enums) return;
		try {
			this.PRODUCT_CATEGORY = this.enums.PRODUCT_CATEGORY;
			this.ADHESIVE = this.enums.ADHESIVE;
			this.HARDNESS_TESTER = this.enums.HARDNESS_TESTER;
			this.HARDNESS_TESTER_TOLERANCE = this.enums.HARDNESS_TESTER_TOLERANCE;
			this.TIM_TYPE = this.enums.TIM_TYPE;
			this.SHAPE = this.enums.SHAPE;
			this.ADHESIVE_MODEL = this.enums.ADHESIVE_MODEL;
			this.PULLTAB_LENGTH = this.enums.PULLTAB_LENGTH;
			this.PULLTAB_CONSTANT = this.enums.PULLTAB_CONSTANT;
			this.LAYER_CONSTANT = this.enums.LAYER_CONSTANT;
			this.THERMAL_COEFFICIENT = this.enums.THERMAL_COEFFICIENT;
			this.VIEW_ANGLE = this.enums.VIEW_ANGLE;
			this.ALGO_TYPE = this.enums.ALGO_TYPE;
			this.INCLUDE_THICKNESS = this.enums.INCLUDE_THICKNESS;
		} catch (error) {
			console.error('Error updating enum values:', error);
		}
	}

	// 創建一個初始化方法
	private initializeComponent = () => {
		// 查詢產品並取得型號list
		const fetches = from(Object.keys(this.TIM_TYPE)).pipe(
			map(key => {
				// 依TIM種類設置型號及導熱係數選項
				const type = this.TIM_TYPE[key]
				this.modelOptions[type] = [];
				this.wmkOptions[key] = [];
				this.wmkOptions[type] = this.THERMAL_COEFFICIENT[key];
				return this.fetchProductList(type);
			}),
			concatMap(fetch => fetch)
		);
		
		// 預設參數
		forkJoin([fetches]).subscribe(() => {
			this.route.queryParams.pipe(
			tap(params => {
				const newParam = new AutoDrawModels.Tim();
				this.setDefaultParam(newParam);
				newParam.tim_model = params['pn'];
				newParam.tim_type = parseInt(params['type']);
				this.syncParam(newParam, 'PRODUCT_NUMBER');
				this.params.push(newParam);
				setTimeout(() => this.generalServ.initTooltips(), 0);
			}),
			).subscribe();
		});
	}

	/**
	 * 產品查詢API
	 * @param type  `number`: tim type
	 * @param index `number`: modelOptions 中目標index
	 */
	public fetchProductList(type: number) {
		let param = {
			type: type,
			wmk_min: null,  // 導熱係數最小
			wmk_max: null,  // 導熱係數最大
			thickness_min: null,  // 厚度最小（導熱硅膠才有）
			thickness_max: null,  // 厚度最大（導熱硅膠才有）
			hardness_min: null, // 硬度最小（導熱硅膠才有）
			hardness_max: null,  // 硬度最大（導熱硅膠才有）
			language_type: 1,  // 語言類型
			is_return_feature: 1  // 是否回傳特型(0 否 1 是)
		}
		this.spinnerState.dispatch(new OpenAction('Loading...'));
		return this.productServ.fetchList(param).pipe(
			takeUntil(this.unsubscribe$),
			catchError(err => {
				this.spinnerState.dispatch(new CloseAction());
				this.errServ.httpErrorHandle(err);
				return of()
			}),
			map(res => res.Data),
			tap((data: ProductModels.Product[]) => {
				this.modelOptions[type] = data;
				this.spinnerState.dispatch(new CloseAction());
				this.cd.markForCheck()
			})
		);
	}

	/**
	 * 送出表單參數
	 */
	public submitParam(algoType: number) {
		if (this.paramCheck()) return;
		const param = {
			product_category: this.PRODUCT_CATEGORY.tim,
			tims: this.params
		}
		this.spinnerState.dispatch(new OpenAction('Loading...'));
		this.autoDrawServ.create_records(param).pipe(
			takeUntil(this.unsubscribe$),
			catchError(err => {
				this.spinnerState.dispatch(new CloseAction());
				this.errServ.httpErrorHandle(err);
				return of()
			}),
			map(res => res.Data),
			tap((data: number) => {
				this.spinnerState.dispatch(new CloseAction());
				this.cd.markForCheck();
				sessionStorage.setItem('type', algoType.toString());
				sessionStorage.setItem('id', data.toString());
				this.router.navigate(['custom/product/autodraw/result']);
			})
		).subscribe();
	}

	/**
	 * 開啟聯絡我們modal
	 */
	public openContactModal() {
		if(this.paramCheck()) return
		this.project.tims = this.params;
		this.contactUsComponent.openModal();
	}

	/**
	 * 
	 * @returns `boolean`: 
	 */
	public paramCheck() {
		// 輸入檢查
		const hasInvalid = this.params.some(tim => {
			let result = this.checkFormServ.checkNull(tim)		 ||
						this.checkFormServ.checkPositive(tim)	 ||
						this.checkFormServ.checkInt(tim)	 	 ||
						this.checkFormServ.checkMoq(tim)

			// 導熱硅脂不需檢查長寬厚
			if (tim.tim_type !== this.TIM_TYPE['TMP']) {
				result = result || 
						 this.checkFormServ.checkLengthWidth(tim) ||
						 this.checkFormServ.checkThickness(tim);
			}
			return result;
		});

		return hasInvalid;
	}

	/**
	 * 新增
	 */
	public addParam() {
		const newParam = new AutoDrawModels.Tim();
		this.setDefaultParam(newParam);
		this.params.push(newParam);
		setTimeout(() => this.generalServ.initTooltips(), 0);
	}

	/**
	 * 設定參數預設值
	 * @param objectIdx `number`: autodraw object index
	 * @param timIdx `number`: tim index
	 */
	public setDefaultParam(param: AutoDrawModels.Tim, timType: number=this.TIM_TYPE['TIM']): AutoDrawModels.Tim{
		// 基本設定
		param.tim_type = timType;
		const typeIsTMP = param.tim_type === this.TIM_TYPE['TMP'];
		// 編號
		const existParam = this.params.find(existParam => existParam === param);
		if (!existParam) {
			const timLength = this.params.length;
			let lastLetter = "";
			if (timLength) lastLetter = <string>this.params[timLength - 1].number;
			lastLetter = lastLetter ? lastLetter : "";	
			param.number = this.alphabetIncrement(lastLetter);
		}
		param.tim_model = null;
		
		// 規格
		param.length = null;
		param.width = null;
		param.thickness = null;
		param.thermal_coefficient = null;
		param.color = null;
		param.hardness = null;
		param.hardness_tester = typeIsTMP ? null : this.HARDNESS_TESTER.SHORE_00;
		param.hardness_tolerance = typeIsTMP ? null : this.HARDNESS_TESTER_TOLERANCE.SHORE_00;
		param.adhesive = this.ADHESIVE.NULL;
		param.adhesive_type = null;	// 預設背膠型號
		param.adhesive_include_thickness = false;

		// 出圖參數
		param.frame_type = false;
		param.date =  moment().toDate();
		param.file_name = null;
		param.drawing_number = null;
		param.quantity = typeIsTMP ? null : 1;
		param.view_angle = typeIsTMP ? null : this.VIEW_ANGLE.FIRST;

		// 重新初始化tooltip
		setTimeout(() => {
			this.generalServ.initTooltips();
		}, 0);

		return param
	}

	/**
	 * 大寫英文字母遞增
	 * @param alphabet `string`: 單個字元的字母
	 * @returns `string`: 遞增後字母
	 */
	public alphabetIncrement(alphabet: string){
		let nextLetter: string;
		nextLetter = alphabet? String.fromCharCode(alphabet.charCodeAt(0) + 1): "A";
		return nextLetter
	}

	/**
	 * 連動指定index的Tim參數
	 * @param objIdx `number`: project object index
	 * @param timIdx `number`: project object tim index
	 * @param type  `string`: 參數大寫key名稱，如HARDNESS_TESTER
	 */
	public syncParam(param: AutoDrawModels.Tim, type: string, timIdx: number = -1){
		const tim_type = param.tim_type || 0;
		const typeIsTMP = tim_type === this.TIM_TYPE['TMP'];
		let newTimModel = param.tim_model;
		let currentProduct = this.getSelectedProduct(tim_type, param.tim_model);

		const calculateThicknessRange = (thickness: number, linkage: Record<string, Record<string, number>>| undefined): string => {
			// 預設值為 linkage 的長度
			let range = String(Object.keys(linkage ?? {}).length);
			
			// 遍歷尋找符合條件的範圍
			for (const [key, rangeObj] of Object.entries(linkage ?? {})) {
				if (thickness < rangeObj['t_max']) {
					range = key;
					break;
				}
			};
			return range;
		};
		
		const thickness = <number>param.thickness ?? 1;
		const thickness_range = calculateThicknessRange(thickness, currentProduct?.features?.linkage);
		// const isThin = thickness < 1 ? 1 : 2;	// 厚度是否為較薄的
		switch (type) {
			case 'TIM_TYPE':
				// TIM種類變動影響: 1. 重置輸入
				this.setDefaultParam(param, <number>param.tim_type);
				break
			case 'LENGTH':
			case 'WIDTH':
			case 'THICKNESS':
				// 厚度影響: 1.超出規格型號清空, 2.規格內變動連動硬度, 3.層數, 4.首個專案名稱, 5.圖號, 6.檔名, 7.出貨方式
				// 長/寬影響: 1.首個專案名稱, 2.層數, 3.圖號, 4.檔名, 5.出貨方式
				if(type === 'THICKNESS' && tim_type !== this.TIM_TYPE['TMP'] && param.tim_model && currentProduct){
					const rangeMin = <number>currentProduct.features?.thickness_min;
					const rangeMax = <number>currentProduct.features?.thickness_max;
					if (param.hardness_tester === this.HARDNESS_TESTER.SHORE_C) {
						const specHardness = currentProduct.features?.linkage[thickness_range]['h_shore_c']
						// const specHardness = isThin===1 ? currentProduct.features?.hardness_thin_C : currentProduct.features?.hardness_thick_C;
						param.hardness = Math.round(<number>specHardness);
					} else {
						const specHardness = currentProduct.features?.linkage[thickness_range]['h_shore_00']
						// const specHardness = isThin===1 ? currentProduct.features?.hardness_thin_00 : currentProduct.features?.hardness_thick_00;
						param.hardness = Math.round(<number>specHardness);
					}
				}
				// 根據厚度計算層數
				this.calcLayer(param);
				// 計算最小可使用手撕位長度
				this.calcAdhesiveLength(param);
				// 預設檔名圖號
				const dateStr = moment().format('YYMMDD');
				const defaultStr = `${param.length}_${param.width}_${thickness}_TIM${dateStr}`;
				param.file_name = defaultStr;
				param.drawing_number = defaultStr;
				break
			case 'THERMAL_COEFFICIENT':
				newTimModel = null;
				// 根據厚度計算層數
				this.calcLayer(param);
				break
			case 'HARDNESS_TESTER':
				// 硬度計影響: 1.變更硬度計對應硬度公差
				param.hardness_tolerance = param.hardness_tester === this.HARDNESS_TESTER.SHORE_00 ?
				this.HARDNESS_TESTER_TOLERANCE.SHORE_00 : this.HARDNESS_TESTER_TOLERANCE.SHORE_C;
				if(tim_type !== this.TIM_TYPE['TMP'] && param.tim_model && currentProduct){
					if (param.hardness_tester === this.HARDNESS_TESTER.SHORE_C) {
						const specHardness = currentProduct.features?.linkage[thickness_range]['h_shore_c']
						param.hardness = Math.round(<number>specHardness);
					} else {
						const specHardness = currentProduct.features?.linkage[thickness_range]['h_shore_00']
						param.hardness = Math.round(<number>specHardness);
					}
				}
				break
			case 'HARDNESS':
				// 硬度影響: 1.超出規格清空型號
				if(currentProduct && thickness){
					if (param.hardness_tester === this.HARDNESS_TESTER.SHORE_00) {
						const spec_h_min = currentProduct.features?.linkage[thickness_range]['00_range_min'];
						const spec_h_max = currentProduct.features?.linkage[thickness_range]['00_range_max'];
						const isOverSpec = !param.hardness || !spec_h_min || !spec_h_max || 
											param.hardness < spec_h_min || param.hardness > spec_h_max;
						if(isOverSpec) newTimModel = null;
					} else {
						const specHardness = currentProduct.features?.linkage[thickness_range]['h_shore_c']
						const isOverSpec = specHardness !== param.hardness;
						if(isOverSpec) newTimModel = null;
					}
					
				}
				break
			case 'ADHESIVE':
				// 滑刀數/背膠影響: 1.出貨方式, 2.有背膠預設背膠型號無則清空
				// 判斷是否有背膠 預設使用'3M9448A' 並設置預設膠厚度
				const hasAdhesive = param.adhesive !== this.ADHESIVE.NULL;
				param.adhesive_type = hasAdhesive ? this.ADHESIVE_MODEL[0].model : null;	// 背膠預設3M9448A
				param.adhesive_include_thickness = hasAdhesive ? param.adhesive_include_thickness : false;
				// 設置預設背膠厚度
				this.setAdhesiveDefault(param);
				break
			case 'ADHESIVE_TYPE':
				// 設置預設背膠厚度
				this.setAdhesiveDefault(param);
				break
			case 'SHAPE':
				// 形狀影響: 1.滑刀數改為0, 2.異形則清空 3.出貨方式
				param.slide_cut = param.shape === this.SHAPE.regular? 0 : 0;
				if(param.shape !== this.SHAPE.regular) newTimModel = null;
				break
			case 'PULLTAB':
				// 手撕位影響: 1.若有則計算可用手撕位無則清空, 2.出貨方式
				if (param.pulltab) {
					this.calcAdhesiveLength(param);
				} else {
					param.pulltab_length = null;
				}
				break
			case 'PRODUCT_NUMBER':
				// 產品型號變動修改導熱係數、顏色、硬度計、硬度公差、硬度
				if (currentProduct) {
					param.shape = this.SHAPE.regular;
					param.thermal_coefficient = Number(currentProduct.features?.thermal_conductivity) || null;
					param.color = <string>currentProduct.features?.color || null;
					param.hardness_tester = typeIsTMP ? null : this.HARDNESS_TESTER.SHORE_00;
					param.hardness_tolerance = typeIsTMP ? null : this.HARDNESS_TESTER_TOLERANCE.SHORE_00;
					if (!param.thickness && !typeIsTMP) {
						param.thickness = 1;
						param.layer = 1;
					}
					param.hardness = !typeIsTMP && param.thickness ? this.getHardness(thickness_range, currentProduct) : null;
				}
				this.calcLayer(param);
				break
			default:
				break
		}
		param.tim_model = newTimModel;
	}


	/**
	 * 設置預設被膠厚度
	 * @param tim `AutoDraw.Tim`
	 */
	public setAdhesiveDefault(tim: AutoDrawModels.Tim) {
		const adhesive = this.ADHESIVE_MODEL.find(adhesive => adhesive.model === tim.adhesive_type);
		tim.adhesive_thickness = adhesive ? adhesive.thickness : null;
	}

	/**
	 * 計算最小可用的手撕位長度
	 * @param tim `AutoDraw.Tim`
	 */
	public calcAdhesiveLength(tim: AutoDrawModels.Tim){
		// 手撕位寬幅可選項應 > 長度(d1)
		const pullTabLengthList = this.PULLTAB_LENGTH.filter((length: number) => {
			return length > <number>tim.length;
		});

		// 預設值條件：手撕位寬幅 >= 長度(d1) + 3 的最小值
		const minLength = <number>tim.length + this.PULLTAB_CONSTANT.SIDE_BUFFER;
		
		// 重新組成該tim手撕位長度選單
		const timNumber = <string>tim.number;
		this.pulltabLengthLists[timNumber] = pullTabLengthList;
		// 設置符合條件的預設值
		const defautIdx = pullTabLengthList.findIndex(length => length >= minLength);
		if (tim.pulltab && defautIdx >= 0) {
			tim.pulltab_length = pullTabLengthList[defautIdx] || null
		} else if (tim.pulltab) {
			// 若無符合條件的預設值且還有可選項則用第一個
			const hasOptions = this.pulltabLengthLists[timNumber].length
			tim.pulltab_length = hasOptions? this.pulltabLengthLists[timNumber][0] : null;
		} else {
			tim.pulltab_length = null
		}

	}

	/**
	 * 取得硬度
	 * @param thickness `number` 厚度
	 * @param product `ProductModels.Product`
	 * @returns `number` 厚度
	 */
	public getHardness(thickness_range: string, product: ProductModels.Product): number {
		// const isThin = thickness < 1 ? 1 : 2;
		const hardness = product.features?.linkage[thickness_range]['h_shore_00']
		// const hardness = isThin===1 ? product.features?.hardness_thin_00 : product.features?.hardness_thick_00;
		return Math.round(<number>hardness);
	}

	/**
	 * 計算層數
	 * @param tim `AutoDraw.Tim`
	 */
	public calcLayer(tim: AutoDrawModels.Tim) {
		const wmk = <number>tim.thermal_coefficient;
		// 導熱係數是否在範圍中(3~5.5w/mk)
		const wmkIsWithinRange = wmk > this.LAYER_CONSTANT.WMK_RANGE_MIN && wmk <= this.LAYER_CONSTANT.WMK_RANGE_MAX;
		// 判斷層數分母
		const denominator = wmkIsWithinRange ?
			this.LAYER_CONSTANT.WITHIN_RANGE_DENOMINATOR : this.LAYER_CONSTANT.OUT_RANGE_DENOMINATOR;
		// 計算層數
		const layer = Math.ceil(<number>tim.thickness / denominator);
		tim.layer = layer;
	}

	/**
	 * 比對輸入的硬度及導入係數參數和產品規格，回傳是否相符
	 * @param product `ProductModels.Product`: 產品
	 * @param tim `AutoDraw.Tim`: tim
	 * @returns `boolean`
	 */
	public compareTimModel(product: ProductModels.Product, param: AutoDrawModels.Tim): boolean {
		// 比對導熱係數
		let wmk = Number(product?.features?.thermal_conductivity);
		const wmkIsMatch = wmk === param.thermal_coefficient;
		let isMatch = wmkIsMatch;
		return isMatch;
	}

	/**
	 * 刪除參數
	 * @param tim `AutoDrawModels.Tim`
	 */
	public deleteParam(tim: AutoDrawModels.Tim) {
		const flag = confirm(this.translate.instant('AUTO_DRAW.ALERT.DELETE'));
		if (!flag) return
		const targetIdx = this.params.findIndex(param => param === tim);
		this.params.splice(targetIdx, 1);
	}

	/**
	 * 取得對應型號產品
	 * @param tim_type `number | null`: tim產品種類
	 * @param tim_model `string | null`: 型號
	 * @returns `ProductModels.Product`
	 */
	public getSelectedProduct(tim_type: number | null, tim_model: string | null) {
		if(!tim_type) return undefined;
		return this.modelOptions[tim_type].find(product => product.product_number === tim_model);
	}
	
	/**
	 * 將enum object value轉為list
	 * @param obj `Object`: enum object
	 * @returns `any[]`: enum object value list
	 */
	public sortEnumObj(obj: Object): any[]{
		const sortedList = Object.values(obj).sort((a, b) => a - b);
		return sortedList
	}

	/**
	 * 相乘計算
	 * @param numA `number|null`: number 1
	 * @param numB `number|null`: number 2
	 * @returns `number`
	 */
	public timesCalc(numA: number|null, numB: number|null): number {
		try {
			const product = <number>numA * <number>numB;
			const rounded = Math.round(product * 100) / 100;
			return rounded;
		} catch (e) {
			return 0
		}
	}


	/**
	 * 檢查是否有非導熱硅脂的參數
	 * @returns `boolean`: `true` 有非TMP參數, `false` 皆是TMP參數
	 */
	public isAllTmpType(): boolean {
		return this.params.some(parma => parma.tim_type !== this.TIM_TYPE['TMP']);
	}
}
